import * as THREE from 'three';
import { sampleHeight } from '../../shared/waveField';
import { createSeededRandom } from '../utils/random';

export interface CameraTuning {
  distance: number;
  height: number;
  lookAhead: number;
  lag: number;
  clearance: number;
  /**
   * Optional cinematic feel knobs. They are resolved against
   * `CINEMATIC_DEFAULTS` when omitted, so existing tuning objects that only set
   * the five base fields keep working unchanged.
   */
  /** Wave-follow strength: fraction of the local swell folded into the eye height. */
  bob?: number;
  /** Smoothing time (s) for the swell sample — higher is calmer. */
  bobLag?: number;
  /** Maximum bank angle in radians (default 3 degrees). */
  maxRoll?: number;
  /** Smoothing time (s) for the bank angle as it settles back to level. */
  rollLag?: number;
  /** Turn rate (rad/s) that maps to a full bank. */
  turnRateRef?: number;
  /** Averaging window (s) used to derive a spike-free turn rate from heading updates. */
  turnWindow?: number;
  /** Degrees of FOV added at full speed. */
  fovGain?: number;
  /** Smoothing time (s) for the FOV zoom. */
  fovLag?: number;
  /** Extra metres of look-ahead at full speed. */
  lookAheadGain?: number;
  /** Speed (m/s) that counts as "cruise" for FOV/look-ahead response. */
  cruiseSpeed?: number;
}

/** Cinematic defaults, applied when a tuning field is omitted. */
const CINEMATIC_DEFAULTS = {
  bob: 0.55,
  bobLag: 0.55,
  maxRoll: (3 * Math.PI) / 180,
  rollLag: 0.35,
  turnRateRef: 0.55,
  turnWindow: 0.4,
  fovGain: 4,
  fovLag: 0.7,
  lookAheadGain: 7,
  cruiseSpeed: 8,
} as const;

/**
 * Chase framing: keep the authored seat at full scale. The old 0.55/0.66
 * "low hero" scales pulled the eye down and in, which cropped the masts and
 * horizon; restoring 1.0 lets the full vessel compose with sea and sky.
 */
const CAMERA_HEIGHT_SCALE = 1.0;
const CAMERA_DISTANCE_SCALE = 1.0;
/** Minimum gap (m) kept between the eye and a sampled crest. */
const MIN_WAVE_CLEARANCE = 1.4;
/**
 * Height (m) of the look-ahead point above the ship origin. Deck level (the
 * deck sits at y = 1.875) holds the hull in the lower frame while keeping the
 * masts and horizon visible above, instead of the near-waterline aim that let
 * the hull fill the frame.
 */
const LOOK_TARGET_HEIGHT = 2.5;
/** Storm bob: slow deterministic heave layered under the swell follow. */
const STORM_BOB_RATE = 1.9;
const STORM_BOB_AMPLITUDE = 0.16;
const STORM_BOB_SECOND_RATE = 0.7;
const STORM_BOB_SECOND_AMPLITUDE = 0.08;

export interface CameraTarget {
  position: THREE.Vector3;
  heading: number;
  speed: number;
}

export type CameraMode = 'chase' | 'firstPerson';

/**
 * World-space first-person seat, resolved by the caller (Game) from an `fpRig`
 * parented to `ship.group`. Supplying it keeps the FP camera in the same
 * interpolated transform as the locally predicted avatar — no extra smoothing.
 */
export interface FirstPersonWorld {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Smoothstep so the speed response eases in instead of snapping. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function damp(delta: number, tau: number): number {
  return 1 - Math.exp(-delta / Math.max(0.001, tau));
}

/** Impact-shake tuning (module scope so field initialisers can read them). */
const SHAKE_SEED = 20261011;
const SHAKE_MAX = 1;
const SHAKE_TAU = 0.3;
const SHAKE_SCALE = 0.45;

/**
 * Heading-following chase camera.
 *
 * Sits behind the ship's heading (world up, so the horizon never inherits pitch
 * or roll), looks ahead of the bow and smooths exponentially. Cleared above the
 * wave surface by `clearance` using the shared CPU height sampler.
 *
 * Cinematic layer on top of the base chase:
 * - banks up to `maxRoll` into turns and settles back to level,
 * - follows the swell with a damped vertical offset,
 * - widens the FOV with speed for a sense of acceleration,
 * - pushes the look-ahead point further forward the faster the ship moves.
 *
 * First-person mode swaps the chase cinematic for an eye-height seat that keeps
 * only the ship's real motion (the deck heave/pitch/roll carried by `fpWorld`).
 * The switch blends position (lerp) and orientation (slerp) over a short eased
 * world-space transition so it reads as a smooth perspective change, not a snap.
 */
export class CameraRig {
  /** FP mode blend duration (s) — short enough to avoid vection, long enough to avoid a snap. */
  static readonly FP_BLEND_DURATION = 0.2;
  /** FP pitch clamp (radians, ±85°) — keeps sky/deck visible and avoids the gimbal flip. */
  static readonly FP_PITCH_LIMIT = (85 * Math.PI) / 180;
  /** Fallback eye height (m) above the ship origin when no `fpWorld` is supplied (deck 1.9 + head 1.42). */
  static readonly FP_EYE_HEIGHT = 3.32;
  /** Reduced-motion flattening strength (0..1) applied to the ship roll/pitch in FP. */
  static readonly FP_REDUCED_MOTION_FLATTEN = 0.45;

  private readonly desired = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  /** Clean, smoothed seat position (no animated offsets) so the chase lerp never compounds them. */
  private readonly smoothed = new THREE.Vector3();
  private readonly rollAxis = new THREE.Vector3();
  private readonly rollQuat = new THREE.Quaternion();
  private readonly rollUp = new THREE.Vector3();

  /** Scratch pose for the chase branch, so it can be blended without touching the camera. */
  private readonly chasePos = new THREE.Vector3();
  private readonly chaseQuat = new THREE.Quaternion();
  private readonly chaseMatrix = new THREE.Matrix4();
  /** Chase FOV kept separate from the live camera FOV so FP never resets its smoothing state. */
  private chaseFov: number;

  /** Scratch pose for the first-person branch. */
  private readonly fpPos = new THREE.Vector3();
  private readonly fpQuat = new THREE.Quaternion();
  private readonly fpLookLocal = new THREE.Quaternion();
  private readonly fpLevelQuat = new THREE.Quaternion();

  /** FOV captured at construction so speed zoom is relative to the authored value. */
  private readonly baseFov: number;
  /** Standalone fallback sampler, used only when no wave callback is injected. */
  private readonly fallbackSampler: (x: number, z: number) => number;

  /** Damped wave height under the camera, in metres. */
  private waveSmooth = 0;
  /** Current bank angle in radians. */
  private roll = 0;
  /** Leaky integral of heading change, in radians (divided by the window it is a rate). */
  private turnAccum = 0;
  /** Filtered dHeading/dt in rad/s. */
  private turnRate = 0;
  private lastHeading: number | null = null;
  /** Clock only advanced for the fallback sampler. */
  private waveClock = 0;

  // ── Impact shake ────────────────────────────────────────────────────
  /**
   * Reduced-motion gate. Defaults to the OS/browser preference and is also
   * writable so `Game` can mirror its test-hook reduced-motion flag.
   */
  reducedMotion =
    typeof window !== 'undefined' && 'matchMedia' in window
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;
  /**
   * FP comfort option: how strongly the ship's roll/pitch is flattened while
   * `reducedMotion` is enabled (0 = keep full deck attitude, 1 = level horizon).
   * Normal play keeps 1.0 deck motion; motion-sensitive players get ~0.45.
   */
  fpReducedMotionFlatten = CameraRig.FP_REDUCED_MOTION_FLATTEN;
  /** Current shake magnitude (0..1); decays every frame. */
  private shake = 0;
  /** Deterministic phase for the slow thunder rumble under the jitter. */
  private shakeClock = 0;
  /** Deterministic jitter source — no `Math.random()` in the shake. */
  private readonly shakeRng = createSeededRandom(SHAKE_SEED);
  private readonly shakeOffset = new THREE.Vector3();
  private readonly shakeRight = new THREE.Vector3();
  private readonly shakeUp = new THREE.Vector3(0, 1, 0);

  // ── Orbit controls ──────────────────────────────────────────────────
  /** Yaw offset from user drag (radians). */
  orbitYaw = 0;
  /** Pitch offset from user drag (radians). */
  orbitPitch = 0;
  /** Distance multiplier from scroll/pinch zoom. */
  orbitZoom = 1;
  /** Seconds since the last orbit input — drives auto-return. */
  private orbitIdleTime = 0;
  /** How long (s) before auto-return begins. */
  private static readonly ORBIT_IDLE_DELAY = 3;
  /** Time constant (s) for the smooth return to default position. */
  private static readonly ORBIT_RETURN_TAU = 1.2;
  /** Min/max zoom multiplier. */
  private static readonly ZOOM_MIN = 0.4;
  private static readonly ZOOM_MAX = 2.5;
  /** Pitch clamp (radians). */
  private static readonly PITCH_MIN = -0.6;
  private static readonly PITCH_MAX = 1.2;

  // ── Camera mode ─────────────────────────────────────────────────────
  /** Active mode; the chase cinematic is the default. */
  mode: CameraMode = 'chase';
  /**
   * Blend progress: 0 = chase … 1 = first-person. Animated toward `mode` by
   * `setMode()`/`update()`; the rendered blend is eased with smoothstep.
   */
  transition = 0;
  /** Deck-relative FP look (rad, 0 = bow +X); Game writes this from the LookController. */
  readonly fp = { yaw: 0, pitch: 0 };

  /** Call from input handlers to signal orbit activity. */
  pokeOrbit(): void {
    this.orbitIdleTime = 0;
  }

  /** Deck-relative yaw used for camera-relative movement (chase → orbit, FP → look). */
  get movementYaw(): number {
    return this.mode === 'firstPerson' ? this.fp.yaw : this.orbitYaw;
  }

  /**
   * Start the short world-space blend to the requested mode. Input semantics
   * switch immediately (`movementYaw` reads the new mode), while the camera
   * eases over `FP_BLEND_DURATION`. Leaving FP preserves the orbit offsets and
   * restarts the idle timer so the existing auto-return resumes normally.
   */
  setMode(mode: CameraMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    if (mode === 'chase') this.pokeOrbit();
  }

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    public tuning: CameraTuning,
  ) {
    this.camera.up.set(0, 1, 0);
    this.baseFov = camera.fov;
    this.chaseFov = camera.fov;
    this.fallbackSampler = (x, z) => sampleHeight(x, z, this.waveClock);
  }

  snapTo(target: CameraTarget): void {
    // Forced chase — deterministic captures and respawn/teleport always leave
    // FP and clear any in-flight blend.
    this.mode = 'chase';
    this.transition = 0;
    this.chaseFov = this.baseFov;

    this.computeDesired(target);
    this.smoothed.copy(this.desired);
    this.camera.position.copy(this.desired);

    // Drop the animated state so a respawn/teleport never carries momentum.
    this.waveSmooth = 0;
    this.roll = 0;
    this.turnAccum = 0;
    this.turnRate = 0;
    this.lastHeading = null;
    this.shake = 0;
    this.shakeClock = 0;
    this.camera.fov = this.baseFov;
    this.camera.updateProjectionMatrix();

    this.computeLook(target, 0);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(this.lookTarget);
  }

  /**
   * Register an impact (cannon hit, ram, thunder). Magnitude is added to a
   * decaying shake, clamped so a burst of impacts never overshoots. Respects
   * the reduced-motion flag (no-op when enabled).
   */
  addShake(strength: number): void {
    if (this.reducedMotion || !(strength > 0)) return;
    this.shake = Math.min(SHAKE_MAX, this.shake + strength);
  }

  update(
    delta: number,
    target: CameraTarget,
    waveHeight?: (x: number, z: number) => number,
    fpWorld?: FirstPersonWorld,
  ): void {
    const sampler = waveHeight ?? this.fallbackSampler;
    const dt = Math.max(0, delta);
    this.waveClock += dt;

    const blend = this.advanceTransition(dt);

    // Fully chase: today's cinematic path, behaviourally unchanged.
    if (this.mode === 'chase' && this.transition <= 0) {
      this.updateOrbit(dt);
      this.computeChasePose(dt, target, sampler);
      this.applyChasePose();
      return;
    }

    // First-person seat (also the blend endpoint).
    this.computeFirstPersonPose(target, fpWorld);

    // Fully first-person: apply the eye pose directly — no chase lag/bank/bob/
    // storm-bob/speed-FOV/shake.
    if (this.mode === 'firstPerson' && this.transition >= 1) {
      this.camera.position.copy(this.fpPos);
      this.camera.quaternion.copy(this.fpQuat);
      this.camera.up.set(0, 1, 0);
      this.setFov(this.baseFov);
      return;
    }

    // Blend: chase pose is the other endpoint. Orbit auto-return is gated to
    // chase mode, so the offsets freeze while FP (and entering-FP) and only
    // resume once the mode is chase again.
    if (this.mode === 'chase') this.updateOrbit(dt);
    this.computeChasePose(dt, target, sampler);

    this.camera.position.lerpVectors(this.chasePos, this.fpPos, blend);
    this.camera.quaternion.slerpQuaternions(this.chaseQuat, this.fpQuat, blend);
    this.camera.up.set(0, 1, 0);
    this.setFov(this.chaseFov + (this.baseFov - this.chaseFov) * blend);
  }

  /** Advance `transition` toward the current mode and return the eased blend factor. */
  private advanceTransition(dt: number): number {
    const target = this.mode === 'firstPerson' ? 1 : 0;
    if (this.transition !== target) {
      const step = dt / CameraRig.FP_BLEND_DURATION;
      this.transition = clamp(this.transition + (target === 1 ? step : -step), 0, 1);
    }
    return smoothstep(this.transition);
  }

  /** Orbit auto-return + clamps. Gated to chase mode by the caller. */
  private updateOrbit(dt: number): void {
    this.orbitIdleTime += dt;
    if (this.orbitIdleTime > CameraRig.ORBIT_IDLE_DELAY) {
      const returnAlpha = damp(dt, CameraRig.ORBIT_RETURN_TAU);
      this.orbitYaw *= 1 - returnAlpha;
      this.orbitPitch *= 1 - returnAlpha;
      this.orbitZoom += (1 - this.orbitZoom) * returnAlpha;
    }
    this.orbitPitch = clamp(this.orbitPitch, CameraRig.PITCH_MIN, CameraRig.PITCH_MAX);
    this.orbitZoom = clamp(this.orbitZoom, CameraRig.ZOOM_MIN, CameraRig.ZOOM_MAX);
  }

  /**
   * Full chase cinematic, computed into scratch pose + `chaseFov`. Mirrors the
   * previous single-mode update so a pure chase frame is byte-equivalent.
   */
  private computeChasePose(
    dt: number,
    target: CameraTarget,
    sampler: (x: number, z: number) => number,
  ): void {
    // --- Base chase: exponentially ease toward the seat behind the bow.
    this.computeDesired(target);
    this.smoothed.lerp(this.desired, damp(dt, this.tuning.lag));

    // --- Turn rate drives the bank; computed from the heading stream.
    this.trackTurnRate(dt, target.heading);

    // --- Swell follow: damp the sample so the rise/fall is smooth, not jittery.
    const bob = this.tuning.bob ?? CINEMATIC_DEFAULTS.bob;
    const swell = sampler(this.smoothed.x, this.smoothed.z);
    this.waveSmooth += (swell - this.waveSmooth) * damp(dt, this.tuning.bobLag ?? CINEMATIC_DEFAULTS.bobLag);

    // --- Bank into the turn. Negative sign: a left turn (heading increasing)
    // lifts the right wing, so the camera rolls its up-vector to the left.
    const maxRoll = this.tuning.maxRoll ?? CINEMATIC_DEFAULTS.maxRoll;
    const rollTarget =
      -maxRoll *
      clamp(this.turnRate / (this.tuning.turnRateRef ?? CINEMATIC_DEFAULTS.turnRateRef), -1, 1);
    this.roll += (rollTarget - this.roll) * damp(dt, this.tuning.rollLag ?? CINEMATIC_DEFAULTS.rollLag);

    // --- Speed response for look-ahead and FOV zoom.
    const cruise = Math.max(0.001, this.tuning.cruiseSpeed ?? CINEMATIC_DEFAULTS.cruiseSpeed);
    const speedT = smoothstep(clamp(Math.max(0, target.speed) / cruise, 0, 1));

    const fovTarget =
      this.baseFov + (this.tuning.fovGain ?? CINEMATIC_DEFAULTS.fovGain) * speedT;
    const nextFov =
      this.chaseFov + (fovTarget - this.chaseFov) * damp(dt, this.tuning.fovLag ?? CINEMATIC_DEFAULTS.fovLag);
    if (Math.abs(nextFov - this.chaseFov) > 1e-4) {
      this.chaseFov = nextFov;
    }

    // --- Assemble the pose: clean seat plus the swell offset.
    this.chasePos.copy(this.smoothed);
    this.chasePos.y += bob * this.waveSmooth;

    // Storm bob: a slow, deterministic heave layered under the swell follow so
    // the eye keeps moving even in a calmer swell. Vertical only, so the
    // horizon stays level while the seat rides the storm.
    this.chasePos.y +=
      Math.sin(this.waveClock * STORM_BOB_RATE) * STORM_BOB_AMPLITUDE +
      Math.sin(this.waveClock * STORM_BOB_SECOND_RATE + 0.8) * STORM_BOB_SECOND_AMPLITUDE;

    // Keep the eye above the water surface (instantaneous sample, unclamped).
    const clearance = Math.max(this.tuning.clearance, MIN_WAVE_CLEARANCE);
    const floor = sampler(this.chasePos.x, this.chasePos.z) + clearance;
    if (this.chasePos.y < floor) this.chasePos.y = floor;

    // Impact shake: jitter the assembled pose (after smoothing, before look-at).
    this.applyShake(dt);

    this.computeLook(target, speedT);

    // Roll about the actual view axis, then aim — lookAt reads `up` to build the basis.
    this.rollAxis.copy(this.lookTarget).sub(this.chasePos).normalize();
    this.rollQuat.setFromAxisAngle(this.rollAxis, this.roll);
    this.rollUp.set(0, 1, 0).applyQuaternion(this.rollQuat);
    this.chaseMatrix.lookAt(this.chasePos, this.lookTarget, this.rollUp);
    this.chaseQuat.setFromRotationMatrix(this.chaseMatrix);
  }

  /** Write the computed chase pose into the live camera. */
  private applyChasePose(): void {
    this.camera.position.copy(this.chasePos);
    this.camera.quaternion.copy(this.chaseQuat);
    this.camera.up.copy(this.rollUp);
    this.setFov(this.chaseFov);
  }

  /**
   * Resolve the FP eye seat. With `fpWorld` (the normal Game path) this is the
   * world matrix of the deck-parented rig, so ship heave/pitch/roll come along
   * for free. Without it, seat the eye at a fixed height above the deck pose
   * and apply `fp.yaw/pitch` against a level horizon.
   */
  private computeFirstPersonPose(target: CameraTarget, fpWorld?: FirstPersonWorld): void {
    // Pitch clamp (±85°) so the sky/deck stay visible and the Euler never flips.
    this.fp.pitch = clamp(this.fp.pitch, -CameraRig.FP_PITCH_LIMIT, CameraRig.FP_PITCH_LIMIT);

    if (fpWorld) {
      this.fpPos.copy(fpWorld.position);
    } else {
      this.fpPos.set(
        target.position.x,
        target.position.y + CameraRig.FP_EYE_HEIGHT,
        target.position.z,
      );
    }

    // Deck-relative look; -PI/2 maps "yaw 0 = bow +X" onto the camera's -Z view axis.
    this.fpLookLocal.setFromEuler(
      new THREE.Euler(this.fp.pitch, this.fp.yaw - Math.PI / 2, 0, 'YXZ'),
    );

    // World-level look: ship yaw only, no ship pitch/roll. This is the fallback
    // orientation and the reduced-motion flatten target.
    this.fpLevelQuat
      .setFromEuler(new THREE.Euler(0, target.heading, 0, 'YXZ'))
      .multiply(this.fpLookLocal);

    if (fpWorld) {
      this.fpQuat.copy(fpWorld.quaternion).normalize();
    } else {
      this.fpQuat.copy(this.fpLevelQuat);
    }

    // Comfort: reduced motion flattens the ship roll/pitch influence on the FP
    // camera (toward a steadier horizon) while preserving look yaw/pitch.
    const flatten = this.reducedMotion ? this.fpReducedMotionFlatten : 0;
    if (flatten > 0) {
      this.fpQuat.slerp(this.fpLevelQuat, clamp(flatten, 0, 1));
    }
  }

  private setFov(fov: number): void {
    if (Math.abs(fov - this.camera.fov) > 1e-4) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  /**
   * Filtered turn rate. The heading arrives as a stepped server stream, so a raw
   * difference would be an impulse train; a leaky integrator (with unit DC gain
   * once divided by its window) recovers the underlying rad/s without spikes.
   */
  private trackTurnRate(dt: number, heading: number): void {
    if (this.lastHeading === null) {
      this.lastHeading = heading;
      return;
    }

    let dh = heading - this.lastHeading;
    dh = Math.atan2(Math.sin(dh), Math.cos(dh)); // shortest arc across the ±π wrap
    this.lastHeading = heading;

    // Ignore respawns/teleports so the camera does not whip around.
    if (Math.abs(dh) > 0.35) {
      this.turnAccum = 0;
      this.turnRate = 0;
      return;
    }

    const window = Math.max(0.001, this.tuning.turnWindow ?? CINEMATIC_DEFAULTS.turnWindow);
    this.turnAccum = (this.turnAccum + dh) * Math.exp(-dt / window);
    this.turnRate = this.turnAccum / window;
  }

  /**
   * Decaying camera-space jitter, applied after the smoothed pose and before
   * look-at so it never feeds back into the chase smoothing. Uses a seeded RNG
   * so the jitter sequence is deterministic (no `Math.random`).
   */
  private applyShake(dt: number): void {
    this.shake *= Math.exp(-dt / SHAKE_TAU);
    if (this.reducedMotion || this.shake < 1e-4) {
      this.shake = 0;
      return;
    }

    const amplitude = this.shake * SHAKE_SCALE;
    this.shakeClock += dt;
    // Starboard axis from the chase direction: right = forward × up.
    this.shakeRight.set(-this.forward.z, 0, this.forward.x);
    this.shakeOffset
      .set(0, 0, 0)
      .addScaledVector(this.shakeRight, (this.shakeRng() * 2 - 1) * amplitude)
      .addScaledVector(this.shakeUp, (this.shakeRng() * 2 - 1) * amplitude * 0.6);
    this.chasePos.add(this.shakeOffset);

    // Slow thunder rumble: a deterministic vertical heave layered under the
    // high-frequency jitter so lightning reads as a heavy, rolling impact.
    const rumble = Math.sin(this.shakeClock * 22.0) * 0.55 + Math.sin(this.shakeClock * 7.0) * 0.45;
    this.chasePos.y += rumble * amplitude * 0.4;
  }

  private computeDesired(target: CameraTarget): void {
    const yaw = target.heading + this.orbitYaw;
    this.forward.set(Math.cos(yaw), 0, -Math.sin(yaw));
    const height = this.tuning.height * CAMERA_HEIGHT_SCALE;
    const distance =
      (this.tuning.distance * CAMERA_DISTANCE_SCALE + Math.min(1, target.speed / 8) * 2) *
      this.orbitZoom;
    this.desired
      .copy(target.position)
      .addScaledVector(this.forward, -distance);
    this.desired.y = target.position.y + height + Math.sin(this.orbitPitch) * distance;
  }

  /** `speedT` (0..1) pushes the aim point further ahead of the bow as speed builds. */
  private computeLook(target: CameraTarget, speedT: number): void {
    const ahead =
      this.tuning.lookAhead + (this.tuning.lookAheadGain ?? CINEMATIC_DEFAULTS.lookAheadGain) * speedT;
    this.lookTarget
      .copy(target.position)
      .addScaledVector(this.forward, ahead);
    // Aim near the waterline ahead of the bow so the hull cutting through the
    // sea is in frame, rather than a top-down view of the deck.
    this.lookTarget.y = target.position.y + LOOK_TARGET_HEIGHT;
  }
}
